import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { questionService } from 'src/data/api/helios-service/questionService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  CreateQuestionRequest,
  GetQuestionRequest,
  ListQuestionsRequest,
  Question,
  SearchQuestionRequest,
  UpdateQuestionRequest,
} from 'src/types/helios/Question';
import {
  handleError,
  isVersionConflictError,
  getFullErrorData,
} from 'src/utils/generalUtils';
import { processQuestionMediaUrls } from 'src/helper/questionHelper';

export interface QuestionState {
  questionsList: Question[];
  question?: Question;
  currentSelectedQuestion?: Question;
  fetchQuestionsApiStatus: ApiRequestStatus;
  searchQuestionApiStatus: ApiRequestStatus;
  createQuestionApiStatus: ApiRequestStatus;
  updateQuestionApiStatus: ApiRequestStatus;
  getQuestionByIdApiStatus: ApiRequestStatus;
}

const initialState: QuestionState = {
  questionsList: [],
  question: undefined,
  fetchQuestionsApiStatus: initApiStatus,
  createQuestionApiStatus: initApiStatus,
  updateQuestionApiStatus: initApiStatus,
  searchQuestionApiStatus: initApiStatus,
  getQuestionByIdApiStatus: initApiStatus,
};

export const fetchQuestionsApiThunk = createAsyncThunk(
  'question/fetchQuestion',
  async (data: ListQuestionsRequest, { rejectWithValue }) => {
    try {
      const response = await questionService.getAll(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(handleError(error));
    }
  },
);
export const fetchQuestionByIdApiThunk = createAsyncThunk(
  'question/fetchQuestionById',
  async (data: GetQuestionRequest, { rejectWithValue }) => {
    try {
      const response = await questionService.getById(data);

      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createQuestionApiThunk = createAsyncThunk(
  'question/createQuestion',
  async (data: CreateQuestionRequest, { rejectWithValue }) => {
    try {
      const response = await questionService.create(data);

      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const searchQuestionApiThunk = createAsyncThunk(
  'question/searchQuestion',
  async (data: SearchQuestionRequest, { rejectWithValue }) => {
    try {
      const response = await questionService.searchQuestions(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateQuestionApiThunk = createAsyncThunk(
  'question/updateQuestion',
  async (data: UpdateQuestionRequest, { rejectWithValue }) => {
    try {
      const response = await questionService.update(data);
      return {
        question: response.question,
        message: 'Question updated successfully',
      };
    } catch (error: unknown) {
      // For version conflicts, preserve full error data
      if (isVersionConflictError(error)) {
        return rejectWithValue(JSON.stringify(getFullErrorData(error)));
      }
      return rejectWithValue(handleError(error));
    }
  },
);

const questionSlice = createSlice({
  name: 'question',
  initialState,
  reducers: {
    clearAllApiStatus: (state) => {
      state.createQuestionApiStatus = initApiStatus;
      state.updateQuestionApiStatus = initApiStatus;
    },
    updateQuestion: (state, action: PayloadAction<any>) => {
      state.questionsList = state.questionsList.map((item) =>
        item.questionId === action.payload.questionId
          ? { ...item, ...action.payload.question }
          : item,
      );
    },
    selectCurrentQuestion: (state, action: PayloadAction<Question | undefined>) => {
      state.currentSelectedQuestion = action.payload;
    },
    clearQuestionData: (state) => {
      state.question = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      //get questions list
      .addCase(fetchQuestionsApiThunk.pending, (state) => {
        state.fetchQuestionsApiStatus.isLoading = true;
        state.fetchQuestionsApiStatus.errorMessage = null;
      })
      .addCase(fetchQuestionsApiThunk.fulfilled, (state, action) => {
        state.fetchQuestionsApiStatus.isLoading = false;
        state.questionsList = action.payload.questions;
        state.fetchQuestionsApiStatus.nextToken = action.payload.nextToken;
      })
      .addCase(fetchQuestionsApiThunk.rejected, (state, action) => {
        state.fetchQuestionsApiStatus.isLoading = false;
        state.fetchQuestionsApiStatus.errorMessage = action.payload as string;
      });
    //get Question By Id
    builder
      .addCase(fetchQuestionByIdApiThunk.pending, (state) => {
        state.getQuestionByIdApiStatus.isLoading = true;
        state.getQuestionByIdApiStatus.errorMessage = null;
        state.question = undefined;
      })
      .addCase(fetchQuestionByIdApiThunk.fulfilled, (state, action) => {
        state.getQuestionByIdApiStatus.isLoading = false;
        state.question = processQuestionMediaUrls(action.payload.question);
      })
      .addCase(fetchQuestionByIdApiThunk.rejected, (state, action) => {
        state.getQuestionByIdApiStatus.isLoading = false;
        state.getQuestionByIdApiStatus.errorMessage = action.payload as string;
      })

      //search questions
      .addCase(searchQuestionApiThunk.pending, (state) => {
        state.searchQuestionApiStatus.isLoading = true;
        state.searchQuestionApiStatus.errorMessage = null;
        state.questionsList = [];
        state.searchQuestionApiStatus.nextToken = undefined;
      })
      .addCase(searchQuestionApiThunk.fulfilled, (state, action) => {
        state.searchQuestionApiStatus.isLoading = false;
        state.questionsList = action.payload.questions;
        state.searchQuestionApiStatus.nextToken = action.payload.nextToken;
      })
      .addCase(searchQuestionApiThunk.rejected, (state, action) => {
        state.searchQuestionApiStatus.isLoading = false;
        state.questionsList = [];
        state.searchQuestionApiStatus.errorMessage = action.payload as string;
      })

      // Create Question cases`
      .addCase(createQuestionApiThunk.pending, (state) => {
        state.createQuestionApiStatus.isLoading = true;
        state.createQuestionApiStatus.errorMessage = null;
        state.createQuestionApiStatus.successMessage = null;
      })
      .addCase(createQuestionApiThunk.fulfilled, (state, action) => {
        state.createQuestionApiStatus.isLoading = false;
        state.createQuestionApiStatus.isSuccess = true;
        state.currentSelectedQuestion = {
          ...state.currentSelectedQuestion,
          programId: action.meta.arg.programId,
          divisionName: action.meta.arg.divisionName,
          businessUnit: action.meta.arg.businessUnit,
          language: action.meta.arg.defaultLanguage,
          version: action.payload.version,
          questionId: action.payload.questionId,
        } as Question;
      })
      .addCase(createQuestionApiThunk.rejected, (state, action) => {
        state.createQuestionApiStatus.isLoading = false;
        state.createQuestionApiStatus.isSuccess = false;
        state.createQuestionApiStatus.errorMessage = action.payload as string;
        state.createQuestionApiStatus.successMessage = null;
      })

      // update Question cases
      .addCase(updateQuestionApiThunk.pending, (state) => {
        state.updateQuestionApiStatus.isLoading = true;
        state.updateQuestionApiStatus.errorMessage = null;
        state.updateQuestionApiStatus.successMessage = null;
      })
      .addCase(updateQuestionApiThunk.fulfilled, (state, action) => {
        state.updateQuestionApiStatus.isLoading = false;
        state.updateQuestionApiStatus.isSuccess = true;
        state.currentSelectedQuestion = {
          ...state.currentSelectedQuestion,
          ...action.payload.question,
        } as Question;
        state.updateQuestionApiStatus.successMessage = action.payload.message;
      })
      .addCase(updateQuestionApiThunk.rejected, (state, action) => {
        state.updateQuestionApiStatus.isLoading = false;
        state.updateQuestionApiStatus.isSuccess = false;
        state.updateQuestionApiStatus.errorMessage = action.payload as string;
        state.updateQuestionApiStatus.successMessage = null;
      });
  },
});

export const {
  clearAllApiStatus,
  updateQuestion,
  selectCurrentQuestion,
  clearQuestionData,
} = questionSlice.actions;
export default questionSlice.reducer;
